import React, { useEffect, useState, useCallback } from 'react';
import { listSchedules, cancelSchedule } from '../api/schedules';
import { authStore } from '../api/client';
import { formatDateTime, statusColor } from '../utils/format';
import ScheduleForm from '../components/interviews/ScheduleForm';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${colorClass}`}>
      {label}
    </span>
  );
}

/** Auto-dismissing success toast - visible for ~3 s. */
function Toast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 bg-white border border-emerald-200
                 text-emerald-900 text-sm font-medium px-5 py-3 rounded-xl shadow-lg
                 flex items-center gap-2 animate-fade-in"
    >
      <span className="text-emerald-600" aria-hidden="true">✓</span>
      {message}
    </div>
  );
}

/** Copy invite deep-link to clipboard. */
function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      console.log('[SchedulesPage] copied invite link');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('[SchedulesPage] clipboard write failed');
    }
  }, [value]);

  if (!value) return <span className="text-slate-600 italic text-xs">-</span>;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={value}
      className="px-3 py-1 text-xs font-medium rounded-lg border border-slate-200
                 text-slate-700 bg-white hover:bg-teal-50/70 hover:border-teal-200 transition-colors"
    >
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  );
}

/** Skeleton row loader. */
function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-2 p-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 bg-slate-200 rounded-lg" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SchedulesPage
// ---------------------------------------------------------------------------

/**
 * SchedulesPage
 *
 * Route: /schedules
 *
 * Lists scheduled interviews for a host email, with create + cancel actions.
 * The "create" form lives in src/components/interviews/ScheduleForm.jsx and
 * is shared with InterviewsPage.
 */
export default function SchedulesPage() {
  // -- Schedules list --
  const [schedules,     setSchedules]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [lookupEmail,   setLookupEmail]   = useState('');
  const [hasFetched,    setHasFetched]    = useState(false);
  const [resolvedEmail, setResolvedEmail] = useState('');

  // -- Create form --
  const [formOpen, setFormOpen] = useState(false);

  // -- Toast --
  const [toast, setToast] = useState(null);

  // ---------------------------------------------------------------------------
  // Load schedules
  // ---------------------------------------------------------------------------

  const loadSchedules = useCallback(async (email) => {
    if (!email?.trim()) return;
    setError('');
    setLoading(true);
    console.log('[SchedulesPage] loading schedules for', email);

    try {
      const res  = await listSchedules(email.trim());
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.content ?? res.data?.schedules ?? []);
      console.log('[SchedulesPage] loaded', list.length, 'schedules');
      setSchedules(list);
      setResolvedEmail(email.trim());
      setHasFetched(true);
    } catch (err) {
      console.error('[SchedulesPage] listSchedules failed:', err.message);
      setError(err.message || 'Failed to load schedules.');
    } finally {
      setLoading(false);
    }
  }, []);

  // -- Auto-load using the authenticated user's email --
  useEffect(() => {
    const email = authStore.user?.email;
    console.log('[SchedulesPage] mount - authenticated user email:', email);
    if (email) {
      setLookupEmail(email);
      loadSchedules(email);
    }
  }, [loadSchedules]);

  const handleLookup = useCallback((e) => {
    e.preventDefault();
    loadSchedules(lookupEmail);
  }, [lookupEmail, loadSchedules]);

  // ---------------------------------------------------------------------------
  // Form callbacks
  // ---------------------------------------------------------------------------

  const handleFormSuccess = useCallback(async (newSchedule) => {
    console.log('[SchedulesPage] schedule created - refreshing list', newSchedule?.id);
    setFormOpen(false);
    setToast('Interview scheduled - invite sent to candidate email');

    const email = newSchedule?.hostEmail || resolvedEmail || lookupEmail;
    if (email) {
      setLookupEmail(email);
      await loadSchedules(email);
    }
  }, [resolvedEmail, lookupEmail, loadSchedules]);

  // ---------------------------------------------------------------------------
  // Cancel schedule
  // ---------------------------------------------------------------------------

  const handleCancel = useCallback(async (schedule) => {
    const displayName = schedule.title || schedule.candidateEmail || `#${schedule.id}`;
    if (!window.confirm(`Cancel the interview "${displayName}"? This cannot be undone.`)) return;

    console.log('[SchedulesPage] cancelling schedule', schedule.id);
    try {
      await cancelSchedule(schedule.id);
      console.log('[SchedulesPage] cancelled - refreshing list');
      await loadSchedules(resolvedEmail);
    } catch (err) {
      console.error('[SchedulesPage] cancelSchedule failed:', err.message);
      setError(err.message || 'Failed to cancel schedule.');
    }
  }, [resolvedEmail, loadSchedules]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const hostEmail = authStore.user?.email    || '';
  const hostName  = authStore.user?.fullName || '';

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Scheduling</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Schedules</h1>
          <p className="text-sm text-ink-faint mt-0.5">Manage upcoming interview sessions.</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((o) => !o)}
          className={`btn btn-md ${formOpen ? 'btn-secondary' : 'btn-primary'} self-start sm:self-auto`}
        >
          {formOpen ? 'Cancel' : '+ New interview'}
        </button>
      </div>

      {/* ── Main Content ────────────────────────────────────────────────── */}
      <div className="space-y-8">

        {/* ── Create Form (collapsible, shared component) ────────────────── */}
        {formOpen && (
          <section>
            <ScheduleForm
              defaultHostEmail={hostEmail}
              defaultHostName={hostName}
              onSuccess={handleFormSuccess}
              onCancel={() => setFormOpen(false)}
            />
          </section>
        )}

        {/* ── Schedules List ─────────────────────────────────────────────── */}
        <section>
          {/* Email lookup - always show so host can reload with a different email */}
          <form onSubmit={handleLookup} className="flex flex-col sm:flex-row sm:items-stretch gap-3 mb-6">
            <input
              type="email"
              required
              placeholder="Enter your host email to load schedules…"
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              className="input flex-1 min-w-0"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary shrink-0 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load Schedules'
              )}
            </button>
          </form>

          <div className="card overflow-hidden">
            {loading ? (
              <TableSkeleton />
            ) : error ? (
              <div className="p-6 text-center">
                <p className="text-red-600 text-sm" role="alert">{error}</p>
              </div>
            ) : hasFetched ? (
              schedules.length === 0 ? (
                <div className="text-center py-12 space-y-2">
                  <p className="text-slate-600 text-sm">No scheduled interviews yet.</p>
                  <p className="text-slate-500 text-xs">
                    Use &quot;New interview&quot; to get started.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidate</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Scheduled at</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Invite</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {schedules.map((s) => (
                        <tr key={s.id} className="hover:bg-canvas-subtle/80 transition-colors">
                          <td className="px-4 py-3 text-slate-900 max-w-[180px] truncate">
                            {s.title || <span className="text-slate-400 italic">Untitled</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-800 text-sm">{s.candidateName || '-'}</div>
                            <div className="text-slate-500 text-xs">{s.candidateEmail}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                            {formatDateTime(s.scheduledAt)}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {s.durationMinutes != null ? `${s.durationMinutes}m` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              label={s.status ?? '-'}
                              colorClass={statusColor(s.status)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <CopyButton value={s.inviteDeepLink ?? s.inviteLink ?? s.inviteUrl} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(s.status || '').toUpperCase() === 'SCHEDULED' && (
                              <button
                                type="button"
                                onClick={() => handleCancel(s)}
                                className="px-3 py-1 text-xs font-medium rounded-lg border border-red-200
                                           text-red-700 bg-white hover:bg-red-50 transition-colors"
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="text-center py-12 px-4">
                <p className="text-slate-600 text-sm">
                  Enter your host email above and click &quot;Load schedules&quot; to view your interviews.
                </p>
              </div>
            )}
          </div>
        </section>

      </div>

      {/* ── Success Toast ─────────────────────────────────────────────────── */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

    </div>
  );
}
